import ExcelJS from "exceljs";
import fileDownload from "js-file-download";

import { productForRow, tradePnL } from "./futuresOptionsView";

const BUY_SELL_CODE_BUY = "1";

function sanitizeFilePart(value) {
  return String(value ?? "")
    .trim()
    .replace(/[^\w.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function positionLabel(row) {
  const side = String(row?.buy_sell_code) === BUY_SELL_CODE_BUY ? "Buy" : "Sell";
  if (row?.security_subtype_code === "C") return `${side} Call`;
  if (row?.security_subtype_code === "P") return `${side} Put`;
  return side;
}

function bushels(row, field) {
  const qty = Number(row?.[field]) || 0;
  const factor = Number(row?.multiplication_factor) || 1;
  return qty * factor;
}

/** Date-only for Excel (strip time). Returns Date or "". */
function dateOnly(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return new Date(Date.UTC(+match[1], +match[2] - 1, +match[3]));
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

/** Visible table columns, wide (one field per column). */
const EXPORT_COLUMNS = [
  { header: "Product", getValue: (row, ctx) => ctx.product },
  { header: "Open", getValue: (row) => (row.is_open ? "Open" : "Closed") },
  { header: "Position", getValue: (row) => positionLabel(row) },
  { header: "Trade Date", getValue: (row) => dateOnly(row.trade_date), numFmt: "yyyy-mm-dd" },
  { header: "Orig. Qty", getValue: (row) => bushels(row, "quantity"), numFmt: "#,##0" },
  { header: "Accumulated", getValue: (row) => bushels(row, "quantity_accumulated"), numFmt: "#,##0" },
  { header: "Open Qty", getValue: (row) => bushels(row, "quantity_open"), numFmt: "#,##0" },
  {
    header: "Potential Remaining",
    getValue: (row) => bushels(row, "quantity_potential_remaining"),
    numFmt: "#,##0",
  },
  { header: "Contract", getValue: (row) => row.security_desc_line_1 || "" },
  { header: "Option Expire", getValue: (row) => dateOnly(row.option_expire_date), numFmt: "yyyy-mm-dd" },
  { header: "Strike", getValue: (row) => row.option_strike_price ?? "", numFmt: "0.0000" },
  { header: "Price", getValue: (row) => row.trade_price ?? "", numFmt: "0.0000" },
  { header: "Current Price", getValue: (row) => row.quote_open ?? "", numFmt: "0.0000" },
  { header: "Gain/Loss", getValue: (row) => tradePnL(row), numFmt: "#,##0.00;(#,##0.00)" },
  { header: "Note", getValue: (row) => row.note || "" },
  { header: "Tracer", getValue: (row) => row.tracer_number || "" },
  { header: "Crop Profile Year", getValue: (row) => row.crop_profile_year ?? "" },
  { header: "Crop Profile Commodity", getValue: (row) => row.crop_profile_commodity_name || "" },
  { header: "Crop Profile Name", getValue: (row) => row.crop_profile_name || "" },
];

function worksheetName(year, commodityName) {
  const raw = `${year ?? ""} - ${commodityName || "Commodity"} - Futures Options`.replace(/\s+/g, " ").trim();
  // Excel worksheet name max 31 chars; strip invalid chars
  return raw.replace(/[:\\/?*[\]]/g, "").slice(0, 31);
}

/**
 * Flat Excel: one line per trade (main + each offset), column-header datatable only.
 */
export async function exportFuturesOptionsWorkbook({
  getTransactionHistoryRowsByCommodity,
  getTransactionHistoryRowsByMatch,
  commodityName,
  year,
}) {
  const mains = getTransactionHistoryRowsByCommodity(commodityName) || [];
  const lines = [];
  for (const main of mains) {
    const product = productForRow(main);
    lines.push({ row: main, product });
    for (const offset of getTransactionHistoryRowsByMatch(main.id) || []) {
      lines.push({ row: offset, product });
    }
  }

  if (!lines.length) {
    throw new Error("No trades to export.");
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Tilley";
  const sheet = workbook.addWorksheet(worksheetName(year, commodityName));

  const headerRow = sheet.addRow(EXPORT_COLUMNS.map((col) => col.header));
  headerRow.font = { bold: true };

  for (const line of lines) {
    const values = EXPORT_COLUMNS.map((col) => {
      const raw = col.getValue(line.row, line);
      return raw ?? "";
    });
    const dataRow = sheet.addRow(values);
    EXPORT_COLUMNS.forEach((col, index) => {
      if (!col.numFmt) return;
      dataRow.getCell(index + 1).numFmt = col.numFmt;
    });
  }

  sheet.columns.forEach((column, index) => {
    const header = EXPORT_COLUMNS[index]?.header ?? "";
    let maxLength = header.length;
    column.eachCell({ includeEmpty: false }, (cell) => {
      maxLength = Math.max(maxLength, cell.value == null ? 0 : String(cell.value).length);
    });
    column.width = Math.min(Math.max(maxLength + 2, 14), 56);
  });

  sheet.views = [{ state: "frozen", ySplit: 1 }];
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: lines.length + 1, column: EXPORT_COLUMNS.length },
  };

  const yearPart = sanitizeFilePart(year) || "year";
  const commodityPart = commodityName ? `-${sanitizeFilePart(commodityName)}` : "";
  const buffer = await workbook.xlsx.writeBuffer();
  fileDownload(buffer, `Futures-Options-${yearPart}${commodityPart}.xlsx`);
}
